import { useEffect, useMemo, useState } from 'react';
import type { MerchantPreset } from './types';
import {
  getStaticMerchantPreset,
  loadMerchantPreset,
  registerMerchantPreset,
  resolveMerchantPresetId,
} from './registry';

interface LoadedPreset {
  id: string;
  preset: MerchantPreset;
}

/**
 * Resolves the active merchant preset. Starts synchronously with the eager /
 * neutral preset from the registry (so first-tick behaviour such as YSE's
 * `disableOnMobile` is already right), then swaps in the lazily loaded preset.
 */
export function useMerchantPreset(
  merchant: string | MerchantPreset | undefined | null,
  hostname: string | undefined
) {
  const explicitId = useMemo(() => {
    if (merchant && typeof merchant === 'object') {
      registerMerchantPreset(merchant);
      return merchant.id;
    }
    return typeof merchant === 'string' ? merchant : undefined;
  }, [merchant]);

  const merchantId = useMemo(
    () => resolveMerchantPresetId({ explicit: explicitId, hostname }),
    [explicitId, hostname]
  );

  const [loaded, setLoaded] = useState<LoadedPreset | null>(null);

  useEffect(() => {
    let cancelled = false;
    loadMerchantPreset(merchantId)
      .then(preset => {
        if (!cancelled) setLoaded({ id: merchantId, preset });
      })
      .catch(() => {
        // Keep the static preset; a failed chunk load must not break the widget.
      });
    return () => {
      cancelled = true;
    };
  }, [merchantId]);

  const merchantPresetLoaded = loaded?.id === merchantId;
  const merchantPreset = useMemo(
    () =>
      loaded && loaded.id === merchantId ? loaded.preset : getStaticMerchantPreset(merchantId),
    [loaded, merchantId]
  );

  return { merchantId, merchantPreset, merchantPresetLoaded };
}
